import type { DirectCallMessage } from './direct-call-session';
import type { DirectCallAudioController } from './direct-call-playback';

/**
 * Replay may re-present history; only an unseen, non-historical reply to a
 * human turn that was actually asked can speak, and each replies exactly
 * once.
 *
 * WI 34651: a human turn that was already pending (unanswered) when a
 * resumed conversation hydrates (`OrderedCallConnection.hydrate`) is
 * delivered tagged `historical`, so the previous "only the latest human
 * turn is live" gate never recognized it as eligible - even once the
 * seat's reply arrived live, well after resume, correctly tagged
 * non-historical. That permanently and silently dropped the reply from
 * speech (it still rendered in the transcript, since
 * `DirectCallSession.receive` has no such gate) the moment any newer
 * human turn existed. A live reply is now eligible whenever the human
 * turn it answers was ever asked - historical or live - by a real member
 * of this conversation, UNLESS that turn was already resolved historically
 * (a historical reply for it already exists in the snapshot): a turn you
 * already got an answer to in a past session must stay text-only even if
 * another member replies to it late, but a turn you are still genuinely
 * waiting on must not go silent forever just because you reopened the app
 * while waiting. A single stable turn identity (the conversation id) is
 * used for the whole call, so a reply to an older turn queues behind
 * whatever is already speaking instead of resetting the queue and
 * aborting it (Send must never silently cancel or drop queued speech).
 *
 * WI 34651 follow-up (cross-seat review): a stable per-call turn identity
 * means `DirectCallAudioController.beginTurn` no longer sees a changing
 * `turnId`, so an interrupt (barge-in) or a mute/unmute cycle used to
 * suppress every later reply for the rest of the call, and the 64-reply
 * dedupe cap could never be reclaimed on a long call - both used to reset
 * on each new human turn's id. The highest asked human turn number is now
 * passed as a separate `generation` alongside the stable `conversationId`,
 * so `DirectCallAudioController`/`DirectCallPlayback` can release
 * interrupt/mute suppression and the dedupe cap on each new human turn
 * without ever touching already-queued or in-flight speech.
 */
export class DirectCallTranscriptAudio {
  private readonly seen = new Set<string>();

  update(
    messages: readonly DirectCallMessage[],
    members: readonly string[],
    audio: Pick<DirectCallAudioController, 'beginTurn' | 'receive'>
  ): void {
    const askedTurns = new Set<number>();
    const resolvedHistoricalTurns = new Set<number>();
    let conversationId: string | undefined;
    let hasLiveActivity = false;
    for (const message of messages) {
      if (!message.historical) hasLiveActivity = true;
      if (
        message.role === 'user' &&
        Number.isSafeInteger(message.humanTurnId) &&
        message.humanTurnId! > 0
      ) {
        askedTurns.add(message.humanTurnId!);
        conversationId ??= message.conversationId;
      } else if (
        message.role === 'assistant' &&
        message.historical &&
        Number.isSafeInteger(message.humanTurnId) &&
        message.humanTurnId! > 0
      ) {
        resolvedHistoricalTurns.add(message.humanTurnId!);
      }
    }
    // A purely historical snapshot (nothing live since this session resumed)
    // must stay silent and must not even establish a turn to speak into.
    if (
      !hasLiveActivity ||
      !askedTurns.size ||
      !conversationId ||
      !members.length
    )
      return;
    const generation = `${conversationId}:${Math.max(...askedTurns)}`;
    audio.beginTurn(conversationId, members, generation);
    for (const message of messages) {
      if (message.role !== 'assistant' || this.seen.has(message.id)) continue;
      this.seen.add(message.id);
      if (
        message.historical ||
        message.conversationId !== conversationId ||
        !Number.isSafeInteger(message.humanTurnId) ||
        !askedTurns.has(message.humanTurnId!) ||
        resolvedHistoricalTurns.has(message.humanTurnId!) ||
        !message.senderInstanceId ||
        !members.includes(message.senderInstanceId) ||
        !Number.isSafeInteger(message.turnId) ||
        message.turnId! < 1
      )
        continue;
      audio.receive({
        messageId: message.id,
        turnId: conversationId,
        seatId: message.senderInstanceId,
        text: message.content,
        conversationId: message.conversationId,
        eventTurnId: message.turnId,
        humanTurnId: message.humanTurnId,
      });
    }
  }
}
